'use client'

import { useState } from 'react'
import { Check, Mail, AlertCircle, Loader2, Link, Info } from 'lucide-react'
import { useIsCalendlyConnected, useCalendlySchedules } from '@/lib/integrations/calendly'
import { ScheduleSelector } from '@/components/integrations/calendly'
import { useSendInvite } from '@/hooks/useInvitations'

export interface EmailPromptStepProps {
  appearance: {
    id: string
  }
  guest: {
    id: string
    name: string
    email: string | null
  }
  episode: {
    id: string
    title: string
    recording_date: string
    recording_time: string | null
    meeting_link: string | null
  }
  onClose: () => void
  onSuccess?: () => void
}

export function EmailPromptStep({
  appearance,
  guest,
  episode,
  onClose,
  onSuccess,
}: EmailPromptStepProps) {
  const [selectedScheduleId, setSelectedScheduleId] = useState<string>('')
  const [useCalendly, setUseCalendly] = useState(true)
  const [manualMeetingLink, setManualMeetingLink] = useState(episode.meeting_link || '')
  const [customMessage, setCustomMessage] = useState('')
  const [emailSent, setEmailSent] = useState(false)
  const [skipped, setSkipped] = useState(false)

  const { isConnected: isCalendlyConnected } = useIsCalendlyConnected()
  const { data: schedules } = useCalendlySchedules()
  const sendInvite = useSendInvite()

  const hasCalendlySchedules = isCalendlyConnected && schedules && schedules.length > 0
  const canSend = guest.email && (useCalendly && hasCalendlySchedules ? selectedScheduleId : true)

  const handleSendInvite = async () => {
    if (!canSend) return

    try {
      await sendInvite.mutateAsync({
        appearance_id: appearance.id,
        guest_id: guest.id,
        schedule_id: useCalendly && hasCalendlySchedules ? selectedScheduleId : undefined,
        meeting_link: !useCalendly || !hasCalendlySchedules ? manualMeetingLink : undefined,
        custom_message: customMessage || undefined,
      })

      setEmailSent(true)
      onSuccess?.()
    } catch (error) {
      console.error('Failed to send invite:', error)
    }
  }

  const handleSkip = () => {
    setSkipped(true)
  }

  // Success state after sending email
  if (emailSent) {
    return (
      <div data-testid="email-prompt-sent" className="px-6 py-8 text-center">
        <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
          <Check className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
        </div>
        <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
          Invitation Sent!
        </h3>
        <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-6">
          {guest.name} has been notified about their appearance on &quot;{episode.title}&quot;.
        </p>
        <button
          onClick={onClose}
          data-testid="email-prompt-done-button"
          className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors"
        >
          Done
        </button>
      </div>
    )
  }

  // Skipped state
  if (skipped) {
    return (
      <div data-testid="email-prompt-skipped" className="px-6 py-8 text-center">
        <div className="w-12 h-12 rounded-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center mx-auto mb-4">
          <Check className="w-6 h-6 text-zinc-600 dark:text-zinc-400" />
        </div>
        <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
          Guest Added!
        </h3>
        <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-4">
          {guest.name} has been added to &quot;{episode.title}&quot;.
        </p>
        <div data-testid="email-prompt-skip-info" className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 mb-6 text-left">
          <Info className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700 dark:text-blue-300">
            You can send the invitation later from the episode details page.
          </p>
        </div>
        <button
          onClick={onClose}
          data-testid="email-prompt-done-button"
          className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors"
        >
          Done
        </button>
      </div>
    )
  }

  return (
    <div data-testid="email-prompt-step" className="flex flex-col">
      {/* Success header */}
      <div className="px-6 py-4 text-center border-b border-zinc-200 dark:border-zinc-800">
        <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-3">
          <Check className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
        </div>
        <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          Guest Added Successfully!
        </h3>
        <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">
          {guest.name} has been added to &quot;{episode.title}&quot;
        </p>
      </div>

      {/* Email prompt content */}
      <div className="px-6 py-4 space-y-4">
        <p className="text-sm text-zinc-700 dark:text-zinc-300 font-medium">
          Would you like to send an invitation email now?
        </p>

        {/* No email warning */}
        {!guest.email && (
          <div data-testid="email-prompt-no-email-warning" className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                No email address
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
                This guest doesn&apos;t have an email address. You can add one later and send the invitation from the episode details page.
              </p>
            </div>
          </div>
        )}

        {guest.email && (
          <>
            {/* Email recipient */}
            <div className="flex items-center gap-2 p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800">
              <Mail className="w-4 h-4 text-zinc-400" />
              <span className="text-sm text-zinc-600 dark:text-zinc-300">
                Email will be sent to:{' '}
                <span className="font-medium">{guest.email}</span>
              </span>
            </div>

            {/* Calendly toggle (only show if connected) */}
            {hasCalendlySchedules && (
              <div data-testid="email-prompt-calendly-section" className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useCalendly}
                      onChange={(e) => setUseCalendly(e.target.checked)}
                      data-testid="email-prompt-calendly-toggle"
                      className="w-4 h-4 rounded border-zinc-300 text-[#FF6F00] focus:ring-[#FF6F00]"
                    />
                    <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                      Include Calendly booking link
                    </span>
                  </label>
                </div>

                {useCalendly && (
                  <div data-testid="email-prompt-schedule-selector">
                    <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">
                      Select Schedule
                    </label>
                    <ScheduleSelector
                      value={selectedScheduleId}
                      onChange={setSelectedScheduleId}
                      placeholder="Choose a Calendly schedule"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Manual meeting link (show if not using Calendly or Calendly not connected) */}
            {(!hasCalendlySchedules || !useCalendly) && (
              <div>
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">
                  <span className="flex items-center gap-2">
                    <Link className="w-4 h-4" />
                    Meeting Link
                    <span className="text-zinc-400 font-normal">(optional)</span>
                  </span>
                </label>
                <input
                  type="url"
                  value={manualMeetingLink}
                  onChange={(e) => setManualMeetingLink(e.target.value)}
                  placeholder="https://zoom.us/j/... or similar"
                  data-testid="email-prompt-meeting-link-input"
                  className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#FF6F00]/50 focus:border-[#FF6F00]"
                />
              </div>
            )}

            {/* Custom message */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5">
                Personal Note
                <span className="text-zinc-400 font-normal ml-1">(optional)</span>
              </label>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows={3}
                placeholder="Add a personal note to the invitation..."
                data-testid="email-prompt-custom-message"
                className="w-full px-3 py-2 rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 placeholder-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#FF6F00]/50 focus:border-[#FF6F00] resize-none"
              />
            </div>

            {/* Error message */}
            {sendInvite.isError && (
              <div data-testid="email-prompt-error" className="flex items-start gap-2 p-3 rounded-lg bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800">
                <AlertCircle className="w-5 h-5 text-rose-600 dark:text-rose-400 flex-shrink-0" />
                <p className="text-sm text-rose-700 dark:text-rose-300">
                  Failed to send invitation. Please try again.
                </p>
              </div>
            )}
          </>
        )}

        {/* Info message */}
        <div className="flex items-start gap-2 p-3 rounded-lg bg-zinc-50 dark:bg-zinc-800/50 text-left">
          <Info className="w-4 h-4 text-zinc-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-zinc-500 dark:text-zinc-400">
            You can also send this later from the episode details page.
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-zinc-200 dark:border-zinc-800">
        <button
          type="button"
          onClick={handleSkip}
          disabled={sendInvite.isPending}
          data-testid="email-prompt-skip-button"
          className="px-4 py-2 text-sm font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-50"
        >
          Skip
        </button>
        {guest.email && (
          <button
            type="button"
            onClick={handleSendInvite}
            disabled={!canSend || sendInvite.isPending}
            data-testid="email-prompt-send-button"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#FF6F00] hover:bg-[#FF6F00]/90 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sendInvite.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4" />
                Send Invitation
              </>
            )}
          </button>
        )}
      </div>
    </div>
  )
}
